/**********************************************************************
// @@@ START COPYRIGHT @@@
// AVI SOLAR ENERGY CONFIDENTIAL: NEED TO KNOW ONLY
// Copyright 2018 - 2019
// Avi Solar Energy Pvt Ltd.
// Protected as an unpublished work.
// The computer program listings, specifications and documentation
// herein are the property of Avi Solar Energy Pvt Ltd., or a third party supplier
// and shall not be reproduced, copied, disclosed, or used in whole or in part for
// any reason without the prior express written permission of Avi Solar Energy Pvt Ltd.
// @@@ END COPYRIGHT @@@
// +++ Code modified on 2019/02/25
**********************************************************************/
<template>
  <div>
    <GlobalGraph v-if="graphParameters.has('current') && current_series.length > 0" class="graph" :graphID="1" :series="current_series"
    :xAxisData="current_xAxis" :yAxis="current_yAxis" :loading="currentGraphProgress" :theme="theme" />
    <GlobalGraph v-if="graphParameters.has('voltage') && voltage_series.length > 0" class="graph" :graphID="2" :series="voltage_series"
    :xAxisData="voltage_xAxis" :yAxis="voltage_yAxis" :loading="voltageGraphProgress" :theme="theme" />
    <GlobalGraph v-if="graphParameters.has('power') && power_series.length > 0" class="graph" :graphID="3" :series="power_series"
    :xAxisData="power_xAxis" :yAxis="power_yAxis" :loading="powerGraphProgress" :theme="theme" />
  </div>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import { mapGettersMixin } from '@/util/mixin/index.js'
import service from '@/services/Scb.js'
import GlobalGraph from '@/components/common/GlobalGraph.vue'

import {
  mapMutations
} from 'vuex'

export default {
  mixins: [ mapGettersMixin ],
  props: ['plantId', 'param', 'date', 'graphParameters'],
  components: {
    GlobalGraph
  },
  data() {
    return {
      type: 'inverter',
      current_series: [],
      current_xAxis: [],
      current_yAxis: [{
        name: '',
        axisLabel: {
          formatter: '{value}',
          rotate: 40
        }
      }],
      currentGraphProgress: false,
      voltage_series: [],
      voltage_xAxis: [],
      voltage_yAxis: [{
        name: '',
        axisLabel: {
          formatter: '{value}',
          rotate: 40
        }
      }],
      voltageGraphProgress: false,
      power_series: [],
      power_xAxis: [],
      power_yAxis: [{
        name: '',
        axisLabel: {
          formatter: '{value}',
          rotate: 40
        }
      }],
      powerGraphProgress: false
    }
  },
  computed: {
    ...mapGetters(['getGeneralState']),
    currentLanguage() {
      return this.getGeneralState('language')
    },
    watchParamAndDate() {
      const connectedParam = Object.keys(this.param)
      return `${this.date} ${connectedParam.toString()} ${connectedParam.map(el => this.param[el]).toString()}`
    }
  },
  mounted() {
    this.current_yAxis[0].name = `${this.$t('Current')} (A)`
    this.voltage_yAxis[0].name = `${this.$t('Voltage')} (V)`
    this.power_yAxis[0].name = `${this.$t('Power')} (kW)`
    this.setIntervalWithoutDelay(this.callAllApi)
  },
  methods: {
    ...mapMutations([
      'SET_LAST_CALL_TIME'
    ]),
    callAllApi() {
      let updateTime = moment().format('MM-DD-YYYY H:mm:ss')
      this.SET_LAST_CALL_TIME(updateTime)
      this.callGraphApi()
    },
    callGraphApi() {
      if (this.graphParameters.has('current')) {
        this.currentGraphProgress = true
        this.getStringData()
      }
      if (this.graphParameters.has('power')) {
        this.powerGraphProgress = true
        this.getPowerStringData()
      }
      if (this.graphParameters.has('voltage')) {
        this.voltageGraphProgress = true
        this.getVoltageStringData()
      }
    },
    async getStringData() {
      try {
        let param = this.getParam()
        this.current_series = []
        let response = await service.getStringDataGraph(param)
        this.currentGraphProgress = false
        Object.keys(response.data.message.response).forEach(current => {
          this.current_series.push({
            name: current,
            type: 'line',
            data: response.data.message.response[current]
          })
        })
        this.current_xAxis = response.data.message.axis
      } catch (err) {
        this.currentGraphProgress = false
      }
    },
    async getVoltageStringData() {
      try {
        let param = this.getParam()
        this.voltage_series = []
        let response = await service.getStringVoltageDataGraph(param)
        this.voltageGraphProgress = false
        Object.keys(response.data.message.response).forEach(voltage => {
          this.voltage_series.push({
            name: voltage,
            type: 'line',
            data: response.data.message.response[voltage]
          })
        })
        this.voltage_xAxis = response.data.message.axis
      } catch (err) {
        this.voltageGraphProgress = false
      }
    },
    async getPowerStringData() {
      try {
        let param = this.getParam()
        this.power_series = []
        let response = await service.getStringPowerDataGraph(param)
        this.powerGraphProgress = false
        Object.keys(response.data.message.response).forEach(voltage => {
          this.power_series.push({
            name: voltage,
            type: 'line',
            data: response.data.message.response[voltage]
          })
        })
        this.power_xAxis = response.data.message.axis
      } catch (err) {
        this.powerGraphProgress = false
      }
    },
    getParam() {
      return {
        plantId: this.plantId,
        dateTo: this.date,
        dateFrom: this.date,
        type: this.type,
        idList: this.param
      }
    }
  },
  watch: {
    isReload() {
      if (this.isReload) {
        this.setIntervalWithoutDelay(this.callAllApi)
      }
    },
    watchParamAndDate() {
      this.callAllApi()
    },
    instantRefresh() {
      if (this.instantRefresh) {
        this.setIntervalWithoutDelay(this.callAllApi)
      }
    },
    currentLanguage() {
      this.current_yAxis[0].name = `${this.$t('Current')} (A)`
      this.voltage_yAxis[0].name = `${this.$t('Voltage')} (V)`
      this.power_yAxis[0].name = `${this.$t('Power')} (kW)`
    }
  }
}
</script>

<style scoped>
.graph {
  width: 100%;
  height: 400px;
}
</style>
